import React from 'react';
import { motion } from 'framer-motion';
interface RatioTableProps {
  headers: string[];
  rows: Array<Array<string | number>>;
  highlightRow?: number;
  highlightCol?: number;
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function RatioTable({
  headers,
  rows,
  highlightRow,
  highlightCol,
  label,
  mode = 'support'
}: RatioTableProps) {
  const isStimulus = mode === 'stimulus';
  // Detect multipliers between columns (assuming horizontal table where row[0] is label and row[1...] are numbers)
  // We only do this in support mode
  const multipliers: Array<string | null> = [];
  if (!isStimulus && rows.length > 0) {
    const firstRow = rows[0];
    for (let i = 1; i < firstRow.length - 1; i++) {
      const val1 = Number(firstRow[i]);
      const val2 = Number(firstRow[i + 1]);
      if (!isNaN(val1) && !isNaN(val2) && val1 !== 0) {
        const mult = val2 / val1;
        if (Number.isInteger(mult)) {
          multipliers.push(`×${mult}`);
        } else if (Number.isInteger(val1 / val2)) {
          multipliers.push(`÷${val1 / val2}`);
        } else {
          multipliers.push(null);
        }
      } else {
        multipliers.push(null);
      }
    }
  }
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden" data-id="element-13447">
      {label && <div className={`text-center font-semibold text-slate-700 border-b border-slate-100 ${isStimulus ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm'}`} data-id="element-13448">
          {label}
        </div>}
      <div className={`overflow-x-auto ${isStimulus ? 'p-3' : 'p-4'}`} data-id="element-13449">
        <table className="w-full border-collapse" data-id="element-13450">
          {/* Top arrows row */}
          {!isStimulus && multipliers.some(m => m !== null) && <thead data-id="element-13451">
              <tr data-id="element-13452">
                <th className="p-1" data-id="element-13453"></th>
                {multipliers.map((mult, i) => <th key={`top-arrow-${i}`} colSpan={2} className="p-1 relative h-8" data-id="element-13454">
                    {mult && <div className="absolute inset-0 flex items-center justify-center -translate-x-1/2 left-full w-full z-10" data-id="element-13455">
                        <svg className="w-16 h-6 overflow-visible" viewBox="0 0 100 30" data-id="element-13456">
                          <path d="M 10 25 Q 50 -5 90 25" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" data-id="element-13457" />
                          <polygon points="90,25 85,20 85,30" fill="#8b5cf6" data-id="element-13458" />
                        </svg>
                        <div className="absolute -top-2 bg-purple-100 text-purple-800 text-[10px] font-bold px-1.5 py-0.5 rounded border border-purple-200" data-id="element-13459">
                          {mult}
                        </div>
                      </div>}
                  </th>)}
                {/* Pad remaining columns if any */}
                {Array.from({
              length: Math.max(0, headers.length - multipliers.length * 2 - 1)
            }).map((_, i) => <th key={`pad-${i}`} className="p-1" data-id="element-13460"></th>)}
              </tr>
            </thead>}

          <thead data-id="element-13461">
            <tr data-id="element-13462">
              {headers.map((header, i) => <th key={i} className={`text-center font-bold border-2 ${isStimulus ? 'p-2 text-xs' : 'p-3 text-sm'} ${highlightCol === i ? 'bg-indigo-100 border-indigo-300 text-indigo-900' : 'bg-slate-100 border-slate-300 text-slate-700'}`} data-id="element-13463">
                  {header}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-13464">
            {rows.map((row, rowIndex) => <tr key={rowIndex} className={highlightRow === rowIndex ? 'bg-indigo-50' : 'bg-white'} data-id="element-13465">
                {row.map((cell, cellIndex) => <td key={cellIndex} className={`text-center border-2 border-slate-200 ${isStimulus ? 'p-2 text-xs' : 'p-3 text-sm'} ${highlightRow === rowIndex || highlightCol === cellIndex ? 'font-bold text-indigo-900' : 'text-slate-700'}`} data-id="element-13466">
                    {cell}
                  </td>)}
              </tr>)}
          </tbody>

          {/* Bottom arrows row */}
          {!isStimulus && multipliers.some(m => m !== null) && <tfoot data-id="element-13467">
              <tr data-id="element-13468">
                <td className="p-1" data-id="element-13469"></td>
                {multipliers.map((mult, i) => <td key={`bottom-arrow-${i}`} colSpan={2} className="p-1 relative h-8" data-id="element-13470">
                    {mult && <div className="absolute inset-0 flex items-center justify-center -translate-x-1/2 left-full w-full z-10" data-id="element-13471">
                        <svg className="w-16 h-6 overflow-visible" viewBox="0 0 100 30" data-id="element-13472">
                          <path d="M 10 5 Q 50 35 90 5" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" data-id="element-13473" />
                          <polygon points="90,5 85,10 85,0" fill="#8b5cf6" data-id="element-13474" />
                        </svg>
                        <div className="absolute top-4 bg-purple-100 text-purple-800 text-[10px] font-bold px-1.5 py-0.5 rounded border border-purple-200" data-id="element-13475">
                          {mult}
                        </div>
                      </div>}
                  </td>)}
              </tr>
            </tfoot>}
        </table>
      </div>
    </motion.div>;
}